import { supabase } from '@/integrations/supabase/client';

const CONFIG_KEY = 'presales_password_enabled';

// Site Map Config Keys
const SITE_MAP_VIEWBOX_KEY = 'site_map_viewbox';
const SITE_MAP_CENTER_OFFSET_KEY = 'site_map_center_offset';
const SITE_MAP_BG_IMAGE_KEY = 'site_map_background_image';
const SITE_MAP_NORTH_OFFSET_KEY = 'site_map_north_offset';

// Default values for site map
export const DEFAULT_SITE_MAP_VIEWBOX = '0 0 1500 864';

export interface SiteMapConfig {
  viewBox: string;
  centerOffset?: number;
  backgroundImage?: string;
  /** Compass north offset in degrees (0 = north up, 90 = north points right) */
  northOffsetDegrees?: number;
}

/**
 * Get the password protection enabled status from Supabase
 * Falls back to environment variable if Supabase is unavailable
 */
export async function getPasswordProtectionEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('value')
      .eq('key', CONFIG_KEY)
      .single();

    if (error) {
      // If table doesn't exist or row doesn't exist, fallback to env var
      console.warn('Error fetching site config:', error);
      const envValue = import.meta.env.VITE_PRESALES_PASSWORD_ENABLED;
      // Default to false if env var is not set
      const enabled = envValue === 'true';
      if (import.meta.env.DEV) {
        console.log('Using fallback env var, password protection:', enabled, 'envValue:', envValue);
      }
      return enabled;
    }

    const enabled = data?.value === 'true';
    if (import.meta.env.DEV) {
      console.log('Password protection enabled (from DB):', enabled, 'value:', data?.value);
    }
    return enabled;
  } catch (error) {
    console.error('Error in getPasswordProtectionEnabled:', error);
    // Fallback to environment variable on error
    const envValue = import.meta.env.VITE_PRESALES_PASSWORD_ENABLED;
    const enabled = envValue === 'true';
    if (import.meta.env.DEV) {
      console.log('Using fallback env var (catch), password protection:', enabled);
    }
    return enabled;
  }
}

/**
 * Set the password protection enabled status in Supabase
 */
export async function setPasswordProtectionEnabled(enabled: boolean): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('site_config')
      .upsert({
        key: CONFIG_KEY,
        value: enabled ? 'true' : 'false',
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'key',
      });

    if (error) {
      console.error('Error updating site config:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating site config:', error);
    return false;
  }
}

/**
 * Authentication is now handled by Supabase Auth via AuthContext
 * See src/contexts/AuthContext.tsx for secure authentication implementation
 */

/**
 * Get the site map display configuration from Supabase
 * Returns default values if not configured
 */
export async function getSiteMapConfig(): Promise<SiteMapConfig> {
  try {
    const { data, error } = await supabase
      .from('site_config')
      .select('key, value')
      .in('key', [SITE_MAP_VIEWBOX_KEY, SITE_MAP_CENTER_OFFSET_KEY, SITE_MAP_BG_IMAGE_KEY, SITE_MAP_NORTH_OFFSET_KEY]);

    if (error) {
      console.warn('Error fetching site map config:', error);
      return { viewBox: DEFAULT_SITE_MAP_VIEWBOX };
    }

    const configMap = new Map(data?.map(d => [d.key, d.value]) || []);
    
    return {
      viewBox: configMap.get(SITE_MAP_VIEWBOX_KEY) || DEFAULT_SITE_MAP_VIEWBOX,
      centerOffset: configMap.get(SITE_MAP_CENTER_OFFSET_KEY) 
        ? parseInt(configMap.get(SITE_MAP_CENTER_OFFSET_KEY)!, 10) 
        : undefined,
      backgroundImage: configMap.get(SITE_MAP_BG_IMAGE_KEY) || undefined,
      northOffsetDegrees: configMap.get(SITE_MAP_NORTH_OFFSET_KEY)
        ? parseInt(configMap.get(SITE_MAP_NORTH_OFFSET_KEY)!, 10)
        : undefined,
    };
  } catch (error) {
    console.error('Error in getSiteMapConfig:', error);
    return { viewBox: DEFAULT_SITE_MAP_VIEWBOX };
  }
}

/**
 * Set the site map display configuration in Supabase
 */
export async function setSiteMapConfig(config: Partial<SiteMapConfig>): Promise<boolean> {
  try {
    const updates: { key: string; value: string; updated_at: string }[] = [];
    const timestamp = new Date().toISOString();

    if (config.viewBox !== undefined) {
      updates.push({
        key: SITE_MAP_VIEWBOX_KEY,
        value: config.viewBox,
        updated_at: timestamp,
      });
    }

    if (config.centerOffset !== undefined) {
      updates.push({
        key: SITE_MAP_CENTER_OFFSET_KEY,
        value: config.centerOffset.toString(),
        updated_at: timestamp,
      });
    }

    if (config.backgroundImage !== undefined) {
      updates.push({
        key: SITE_MAP_BG_IMAGE_KEY,
        value: config.backgroundImage,
        updated_at: timestamp,
      });
    }

    if (config.northOffsetDegrees !== undefined) {
      updates.push({
        key: SITE_MAP_NORTH_OFFSET_KEY,
        value: config.northOffsetDegrees.toString(),
        updated_at: timestamp,
      });
    }

    if (updates.length === 0) {
      return true; // Nothing to update
    }

    const { error } = await supabase
      .from('site_config')
      .upsert(updates, { onConflict: 'key' });

    if (error) {
      console.error('Error updating site map config:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in setSiteMapConfig:', error);
    return false;
  }
}

/**
 * Clear a specific site map config value (set to empty/default)
 */
export async function clearSiteMapConfigValue(key: 'viewBox' | 'centerOffset' | 'backgroundImage' | 'northOffsetDegrees'): Promise<boolean> {
  try {
    const keyMap = {
      viewBox: SITE_MAP_VIEWBOX_KEY,
      centerOffset: SITE_MAP_CENTER_OFFSET_KEY,
      backgroundImage: SITE_MAP_BG_IMAGE_KEY,
      northOffsetDegrees: SITE_MAP_NORTH_OFFSET_KEY,
    };

    const { error } = await supabase
      .from('site_config')
      .delete()
      .eq('key', keyMap[key]);

    if (error) {
      console.error('Error clearing site map config value:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in clearSiteMapConfigValue:', error);
    return false;
  }
}

